import {useCallback, useEffect, useState} from 'react';

import useFilters from '@/hooks/useFilters';
import {ratesService} from '@/services/ratesService';
import type {AfriexOTCTrade, AfriexQueryParams} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {OTCTradeListProps} from './types';

const useOTCTradeList = (): OTCTradeListProps => {
  const [otcTradeList, setOtcTradeList] = useState<AfriexOTCTrade[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch] = useState<boolean>(false);
  const [ratesTotal, setRatesTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);
  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});

  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllOTCTrades = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data: items, total} = await ratesService.getOTCTradeList({
          ...params,
          ...currentFilters,
        });
        setOtcTradeList(items);
        setRatesTotal(total ?? 0);
        if (total && total > 10) {
          setShowPagination(true);
        }
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  useEffect(() => {
    // `fetchAllOTCTrades` try/catches into `setError`, so this only fires if
    // that catch block itself throws. Attached because the OTC trade list is
    // what an admin approves rates against, and a stale list that failed to
    // refresh must not look like a current one. Dev logger rather than a toast,
    // because the hook already exposes the real failure through `error`.
    fetchAllOTCTrades({}).catch(logger.error);
  }, [fetchAllOTCTrades]);

  const handleApplyFilters = useCallback(() => {
    const applied = applyFilters(filtersToApply);
    // Pagination reset is success-only work. applyFilters returns false when it
    // rejects a date range, and Pagination reacts to this flag by calling
    // onPageChange -> a refetch, so toggling it unconditionally fired a request
    // the user never asked for. See hooks/useFilters.ts.
    if (!applied) {
      return false;
    }

    setIsPageReset((prev) => !prev);
    return true;
  }, [applyFilters, filtersToApply]);

  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  const handleApprove = useCallback(async (id: string) => {
    try {
      await ratesService.approveOTCTrade(id);
      showSuccessMessage(`OTC rate approved successfully`);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      showErrorMessage(
        `Error approving OTC rate: ${getErrorMessage(e, String(e))}`,
      );
      setError(e);
    }
  }, []);

  return {
    error,
    fetchAllOTCTrades,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: ratesTotal,
    },
    showPagination,
    otcTradeList,
    filterCount,
    isPageReset,
    currentFilters,
    filtersToApply,
    clearFilters: cleanFilters,
    selectAppliedFilters,
    handleApplyFilters,
    selectCurrentFilters,
    handleApprove,
  };
};

export default useOTCTradeList;
